// Pure hex geometry for the minimap.
//
// The 3D board lays tiles out with `axialToWorld` (apps/app/src/render/HexGrid.tsx):
//     world.x = SIZE * sqrt(3) * (q + r / 2)      world.z = SIZE * 1.5 * r
// i.e. a POINTY-TOP hex layout seen from above (rows run along x; +r goes "down" the
// screen when world z maps to screen y). The design spec's flat-top formula does not match
// this board; the minimap uses the same axes as the 3D board so both agree on orientation.
// Unit space here is hex circumradius = 1; screen = unit * scale + pan.

export const SQRT3 = Math.sqrt(3);

export interface Point {
  readonly x: number;
  readonly y: number;
}

export interface Bounds {
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
}

/** Axial neighbour deltas indexed by EDGE: edge i joins corner i and corner i+1. */
export const EDGE_NEIGHBORS: readonly (readonly [number, number])[] = [
  [1, 0], // east
  [0, 1], // south-east
  [-1, 1], // south-west
  [-1, 0], // west
  [0, -1], // north-west
  [1, -1], // north-east
];

/** Corner i sits at angle 60*i - 30 degrees (y down), so corner 5 is the top vertex. */
export const CORNER_COS: readonly number[] = [0, 1, 2, 3, 4, 5].map((i) =>
  Math.cos(((60 * i - 30) * Math.PI) / 180),
);
export const CORNER_SIN: readonly number[] = [0, 1, 2, 3, 4, 5].map((i) =>
  Math.sin(((60 * i - 30) * Math.PI) / 180),
);

/** Hex centre in unit space (circumradius 1). */
export function axialToUnit(q: number, r: number): Point {
  return { x: SQRT3 * (q + r / 2), y: 1.5 * r };
}

/** Six corners of the hex centred at (cx, cy) with circumradius `size`. */
export function hexCorners(cx: number, cy: number, size: number): Point[] {
  return CORNER_COS.map((c, i) => ({ x: cx + size * c, y: cy + size * (CORNER_SIN[i] ?? 0) }));
}

/** Round fractional cube coordinates to the nearest hex (handles negative q/r). */
export function cubeRound(fq: number, fr: number): { q: number; r: number } {
  const fs = -fq - fr;
  let q = Math.round(fq);
  let r = Math.round(fr);
  const s = Math.round(fs);
  const dq = Math.abs(q - fq);
  const dr = Math.abs(r - fr);
  const ds = Math.abs(s - fs);
  if (dq > dr && dq > ds) q = -r - s;
  else if (dr > ds) r = -q - s;
  // Normalise -0 so equality checks and Map keys behave.
  return { q: q + 0, r: r + 0 };
}

/** Unit-space point -> containing hex. */
export function unitToAxial(x: number, y: number): { q: number; r: number } {
  return cubeRound((SQRT3 / 3) * x - y / 3, (2 / 3) * y);
}

/** Unit-space bounding box of a set of hexes (including the hex extents). */
export function boardBounds(tiles: readonly { q: number; r: number }[]): Bounds {
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const t of tiles) {
    const x = SQRT3 * (t.q + t.r / 2);
    const y = 1.5 * t.r;
    if (x - SQRT3 / 2 < minX) minX = x - SQRT3 / 2;
    if (x + SQRT3 / 2 > maxX) maxX = x + SQRT3 / 2;
    if (y - 1 < minY) minY = y - 1;
    if (y + 1 > maxY) maxY = y + 1;
  }
  if (!Number.isFinite(minX)) return { minX: 0, minY: 0, maxX: 1, maxY: 1 };
  return { minX, minY, maxX, maxY };
}

/** Numeric key for a tile, unique for |q|,|r| < 32768. */
export function tileKey(q: number, r: number): number {
  return (q + 32768) * 65536 + (r + 32768);
}
